import { useCallback, useEffect, useState } from 'react';

import { Col, ConfigProvider, notification, PaginationProps, Row } from 'antd';
import locale from 'antd/locale/pt_BR';

import blinketBannerDesktop from 'assets/images/banners/banner-blinket-desktop.png';
import blinketBannerMobile from 'assets/images/banners/banner-blinket-mobile.png';
import sevenBannerDesktop from 'assets/images/banners/banner-seven-desktop.png';
import sevenBannerMobile from 'assets/images/banners/banner-seven-mobile.png';
import dayjs from 'dayjs';
import { getCookie } from 'helpers/cookie';
import { Marketplace, Paginator } from 'interfaces/marketplace';
import { marketplaceService } from 'services/marketplace';
import { IFilter } from 'services/marketplace/types';
import { BLINKET_APP_URL } from 'settings';
import { marketplaceTheme } from 'styles/antd';

import Footer from './Footer';
import Header from './Header';
import ItemCarousel from './ItemCarousel';
import ItemList from './ItemList';
import ListFilter from './ListFilter';
import LocaleModal from './LocaleModal';
import MobileItemList from './MobileItemList';
import styles from './styles.module.css';

import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const MarketPlace = () => {
  const [items, setItems] = useState<Marketplace[]>([]);
  const [fallbackData, setFallbackData] = useState<Marketplace[]>([]);
  const [loading, setLoading] = useState(false);
  const [mobileList, setMobileList] = useState(false);
  const [filters, setFilters] = useState<IFilter>({ page: 1 });
  const [pagination, setPagination] = useState<Paginator>({ itemsPerPage: 8, page: 1, pages: 1, totalItems: 0 });
  const [fallbackPagination, setFallbackPagination] = useState<Paginator>({
    itemsPerPage: 8,
    page: 1,
    pages: 1,
    totalItems: 0
  });
  const [openLocaleModal, setOpenLocaleModal] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState<string>('Brasil');

  useEffect(() => {
    const userId = getCookie('blk_user');

    if (userId) {
      window.gtag('set', 'user_id', userId);
    }
  }, []);

  const onPageChange: PaginationProps['onChange'] = (page: number, pageSize?: number) => {
    setFilters(prev => ({
      ...prev,
      page,
      ...(pageSize ? { itemsPerPage: pageSize } : {})
    }));
  };

  const fetchItems = useCallback(async (filters: IFilter) => {
    try {
      const queryParams = { ...filters };

      if (filters.type === 'online') {
        delete queryParams.region;
      }

      const { data, paginator } = await marketplaceService.eventList(queryParams);
      const pagination = {
        itemsPerPage: paginator.itemsPerPage,
        page: paginator.page,
        pages: paginator.pages,
        totalItems: paginator.totalItems
      };

      return { data, pagination };
    } catch (err) {
      notification.error({ message: `ops... ${err}` });
    }
  }, []);

  const changeFallbackPage = (page: number, pageSize?: number) => {
    setLoading(true);
    fetchItems({ page, ...(pageSize ? { itemsPerPage: pageSize } : {}) }).then(({ data, pagination }) => {
      setFallbackData(data);
      setFallbackPagination(pagination);
    });
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchItems(filters).then(({ data, pagination }) => {
      setItems(data);
      setPagination(pagination);
      setLoading(false);

      if (data.length > 0) {
        setFallbackData([]);
        setFallbackPagination({ itemsPerPage: 8, page: 1, pages: 1, totalItems: 0 });
        return;
      }

      fetchItems({ page: 1 }).then(({ data, pagination }) => {
        setFallbackData(data);
        setFallbackPagination(pagination);
        setLoading(false);
      });
    });
  }, [fetchItems, filters]);

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleLocaleSelect = (locale: string) => {
    if (['Qualquer lugar', 'Brasil'].includes(locale)) {
      setSelectedLocale('Brasil');
      setFilters(prev => ({ ...prev, region: undefined, page: 1 }));
      setOpenLocaleModal(false);
      return;
    }

    if (locale.includes('-')) {
      locale = locale.split('-')[0].trim();
    }

    setSelectedLocale(locale === 'Qualquer lugar' ? 'Brasil' : locale);
    setOpenLocaleModal(false);
    setFilters(prev => ({ ...prev, region: locale, page: 1 }));
  };

  const handleRemoveSearch = () => {
    setFilters(prev => ({ ...prev, search: undefined, page: 1 }));
  };

  const cleanAllFilters = () => {
    setFilters({ page: 1 });
  };

  return (
    <ConfigProvider theme={marketplaceTheme} locale={locale}>
      <Header selectedLocale={selectedLocale} handleSearch={handleSearch} setOpenLocaleModal={setOpenLocaleModal} />
      <div className={styles.alignCenter}>
        <Col xs={0} md={24} className={`${styles.bannerContainer} ${styles.maxWidth}`}>
          <a href='https://sevensummit.eduzz.com/lista-de-espera-seven-2025' target='_blank' rel='noreferrer'>
            <img src={sevenBannerDesktop} className={styles.bannerImage} />
          </a>
        </Col>
        <Col xs={24} md={0} className={styles.bannerContainer}>
          <a href='https://sevensummit.eduzz.com/lista-de-espera-seven-2025' target='_blank' rel='noreferrer'>
            <img src={sevenBannerMobile} className={styles.bannerImage} />
          </a>
        </Col>

        <div className={styles.maxWidth}>
          <ListFilter
            filters={filters}
            setFilters={setFilters}
            selectedLocale={selectedLocale}
            handleRemoveSearch={handleRemoveSearch}
          />

          <Row
            gutter={[10, 10]}
            align={'middle'}
            justify={'space-around'}
            className={`${styles.listContainer} ${styles.maxWidth}`}
          >
            <ItemList
              items={items}
              loading={loading}
              pagination={pagination}
              onPageChange={onPageChange}
              cleanAllFilters={cleanAllFilters}
            />

            {!!fallbackData.length && !loading && (
              <ItemList
                items={fallbackData}
                loading={loading}
                pagination={fallbackPagination}
                onPageChange={onPageChange}
                cleanAllFilters={cleanAllFilters}
                changeFallbackPage={changeFallbackPage}
                isFallback
              />
            )}

            {mobileList ? (
              <>
                <MobileItemList
                  items={items}
                  loading={loading}
                  pagination={pagination}
                  onPageChange={onPageChange}
                  cleanAllFilters={cleanAllFilters}
                />

                {!!fallbackData.length && !loading && (
                  <MobileItemList
                    items={fallbackData}
                    loading={loading}
                    pagination={fallbackPagination}
                    onPageChange={onPageChange}
                    cleanAllFilters={cleanAllFilters}
                    changeFallbackPage={changeFallbackPage}
                    isFallback
                  />
                )}
              </>
            ) : (
              <>
                <ItemCarousel
                  items={items}
                  loading={loading}
                  pagination={pagination}
                  setMobileList={setMobileList}
                  cleanAllFilters={cleanAllFilters}
                />

                {!!fallbackData.length && !loading && (
                  <ItemCarousel
                    items={fallbackData}
                    loading={loading}
                    pagination={fallbackPagination}
                    setMobileList={setMobileList}
                    cleanAllFilters={cleanAllFilters}
                    isFallback
                  />
                )}
              </>
            )}
          </Row>
        </div>
      </div>
      <div
        className={styles.alignCenter}
        style={{
          borderTop: '1px solid #d9d9d9'
        }}
      >
        <Row>
          <Col xs={0} md={24} className={`${styles.bannerContainer} ${styles.maxWidth}`}>
            <a href={`${BLINKET_APP_URL}/event`} target='_blank' rel='noreferrer'>
              <img src={blinketBannerDesktop} className={styles.bannerImage} />
            </a>
          </Col>
          <Col xs={24} md={0} className={styles.bannerContainer}>
            <a href={`${BLINKET_APP_URL}/event`} target='_blank' rel='noreferrer'>
              <img src={blinketBannerMobile} className={styles.bannerImage} />
            </a>
          </Col>
        </Row>
      </div>

      <Footer />

      <LocaleModal open={openLocaleModal} setOpen={setOpenLocaleModal} handleSelect={handleLocaleSelect} />
    </ConfigProvider>
  );
};

export default MarketPlace;
